/**
 * Personal MCP server configuration.
 *
 * This UI uses the repository-style `mcpServers` shape inside the settings API's
 * opaque MCP object. The API does not enforce this shape or resolve credentials.
 * Saving a definition does not establish how a session will use it.
 */

/**
 * Transport the agent uses to reach the server.
 *
 * `local` and `stdio` are spelled differently for historical reasons and validate
 * identically. Prefer `local` for new definitions.
 */
export type McpTransport = 'local' | 'stdio' | 'http' | 'sse'

export const MCP_TRANSPORTS: readonly McpTransport[] = ['local', 'stdio', 'http', 'sse']

/** Transports that carry a `url` and optional `headers`. */
export const MCP_REMOTE_TRANSPORTS: readonly McpTransport[] = ['http', 'sse']

/** Transports that spawn a process and carry `command`, `args`, `env`, and `cwd`. */
export const MCP_PROCESS_TRANSPORTS: readonly McpTransport[] = ['local', 'stdio']

export function isRemoteTransport(type: McpTransport): boolean {
  return MCP_REMOTE_TRANSPORTS.includes(type)
}

export function isProcessTransport(type: McpTransport): boolean {
  return MCP_PROCESS_TRANSPORTS.includes(type)
}

/**
 * Server name constraint, from the schema's `patternProperties` key.
 *
 * Anchored and applied to the whole name. Note this permits `/`, so names may
 * look path-like.
 */
export const MCP_SERVER_NAME_PATTERN = /^[a-zA-Z0-9/_-]+$/

/** `tools: ['*']` opts into every tool the server advertises. */
export const MCP_ALL_TOOLS = '*'

/**
 * One server definition. The union is enforced by `validateMcpServer` rather than
 * by the type, because the form edits a partial draft before it is valid.
 *
 * Remote servers carry `url` and may carry `headers`. Process servers carry
 * `command`, `args`, and may carry `env` and `cwd`. Mixing the two sets is
 * rejected, matching the schema's `oneOf`.
 */
export interface McpServerConfig {
  [key: string]: unknown
  type: McpTransport
  tools: string[]
  command?: string
  args?: string[]
  env?: Record<string, string>
  cwd?: string
  url?: string
  headers?: Record<string, string>
  /** Optional timeout in milliseconds. */
  timeout?: number
}

/** Keyed by server name, matching the schema's `mcpServers` object. */
export type McpServerMap = Record<string, McpServerConfig>

/** Check fields consumed by the UI without discarding opaque server options. */
export function isMcpServerConfig(value: unknown): value is McpServerConfig {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  if (!('type' in value) || !MCP_TRANSPORTS.some(type => type === value.type)) return false
  if (!('tools' in value) || !Array.isArray(value.tools) || !value.tools.every(tool => typeof tool === 'string')) {
    return false
  }
  if ('args' in value && (!Array.isArray(value.args) || !value.args.every(arg => typeof arg === 'string'))) return false
  if ('timeout' in value && typeof value.timeout !== 'number') return false
  for (const [field, record] of Object.entries(value)) {
    if (['url', 'command', 'cwd'].includes(field) && typeof record !== 'string') return false
    if (field !== 'env' && field !== 'headers') continue
    if (
      typeof record !== 'object' ||
      record === null ||
      Array.isArray(record) ||
      !Object.values(record).every(entry => typeof entry === 'string')
    ) {
      return false
    }
  }
  return true
}

/** Apply only editable fields to the latest stored server. */
export function mergeMcpServerForm(existing: McpServerConfig, form: McpServerConfig): McpServerConfig {
  return pruneMcpServer({
    ...existing,
    type: form.type,
    url: form.url,
    headers: form.headers,
    command: form.command,
    args: form.args,
    env: form.env,
    timeout: form.timeout,
  })
}

/**
 * Repository-style reference syntax, retained as text in personal settings.
 * Recognizing a reference does not mean a secret exists or can be resolved.
 */
export const MCP_SECRET_REFERENCE_PATTERN = /\$(?:\{([A-Z_][A-Z0-9_]*)\}|([A-Z_][A-Z0-9_]*))/g

/**
 * Fields a reference may appear in.
 *
 * Fields in which this form accepts repository-style references.
 */
export const MCP_SECRET_REFERENCE_FIELDS = ['env', 'headers'] as const

export type McpSecretReferenceField = (typeof MCP_SECRET_REFERENCE_FIELDS)[number]

export interface McpSecretReference {
  /** Secret name, without the `$` or braces. */
  name: string
  field: McpSecretReferenceField
  /** Key within `env` or `headers` whose value holds the reference. */
  key: string
}

/** Extracts every secret name referenced by a value. */
export function parseSecretReferences(value: string): string[] {
  const names: string[] = []
  // `matchAll` needs its own traversal; the shared regex is stateful.
  for (const match of value.matchAll(new RegExp(MCP_SECRET_REFERENCE_PATTERN))) {
    const name = match[1] ?? match[2]
    if (name) names.push(name)
  }
  return names
}

/** Every secret the definition depends on, in a stable order, deduplicated. */
export function collectSecretReferences(config: McpServerConfig): McpSecretReference[] {
  const found: McpSecretReference[] = []
  const seen = new Set<string>()

  for (const field of MCP_SECRET_REFERENCE_FIELDS) {
    const record = config[field]
    if (!record) continue

    for (const key of Object.keys(record).sort()) {
      for (const name of parseSecretReferences(record[key] ?? '')) {
        const dedupeKey = `${field}:${key}:${name}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        found.push({name, field, key})
      }
    }
  }

  return found
}

/** True when the value contains at least one secret reference. */
export function hasSecretReference(value: string): boolean {
  return parseSecretReferences(value).length > 0
}

export interface McpValidationError {
  /** Dotted path to the offending field, relative to the server definition. */
  path: string
  message: string
}

const FIELDS_REJECTED_BY_REMOTE = ['command', 'args', 'env', 'cwd'] as const
const FIELDS_REJECTED_BY_PROCESS = ['url', 'headers'] as const

/**
 * Validates one server definition against the schema's rules.
 *
 * Returns every problem rather than the first, so the form can mark all bad
 * fields at once. This validates form conventions, not runtime readiness.
 */
export function validateMcpServer(name: string, config: Partial<McpServerConfig>): McpValidationError[] {
  const errors: McpValidationError[] = []

  if (!name.trim()) {
    errors.push({path: 'name', message: 'Enter a name for this server.'})
  } else if (!MCP_SERVER_NAME_PATTERN.test(name)) {
    errors.push({
      path: 'name',
      message: 'Use only letters, numbers, and the characters / _ -',
    })
  }

  const type = config.type
  if (!type) {
    errors.push({path: 'type', message: 'Choose how Copilot connects to this server.'})
  } else if (!MCP_TRANSPORTS.includes(type)) {
    errors.push({path: 'type', message: `"${type}" is not a supported server type.`})
  }

  if (!config.tools || config.tools.length === 0) {
    errors.push({path: 'tools', message: 'List at least one tool, or use * for all tools.'})
  }

  if (config.timeout !== undefined && (!Number.isSafeInteger(config.timeout) || config.timeout <= 0)) {
    errors.push({path: 'timeout', message: 'Enter a positive whole number of milliseconds.'})
  }

  if (type && isRemoteTransport(type)) {
    if (!config.url?.trim()) {
      errors.push({path: 'url', message: 'Enter the server URL.'})
    } else if (!isValidRemoteUrl(config.url)) {
      errors.push({path: 'url', message: 'Enter a valid http or https URL.'})
    }

    for (const field of FIELDS_REJECTED_BY_REMOTE) {
      if (config[field] !== undefined) {
        errors.push({path: field, message: `A ${type} server cannot set ${field}.`})
      }
    }
  }

  if (type && isProcessTransport(type)) {
    if (!config.command?.trim()) {
      errors.push({path: 'command', message: 'Enter the command that starts the server.'})
    }
    if (!config.args) {
      errors.push({path: 'args', message: 'Provide arguments, or an empty list if there are none.'})
    }

    for (const field of FIELDS_REJECTED_BY_PROCESS) {
      if (config[field] !== undefined) {
        errors.push({path: field, message: `A ${type} server cannot set ${field}.`})
      }
    }
  }

  errors.push(...validateSecretPlacement(config))

  return errors
}

/**
 * Rejects secret references outside `env` and `headers`.
 *
 * A reference elsewhere is almost always a mistake that would otherwise be sent
 * to the server verbatim, leaking nothing but failing confusingly. Flag it early.
 */
function validateSecretPlacement(config: Partial<McpServerConfig>): McpValidationError[] {
  const errors: McpValidationError[] = []
  const scalars: Array<[string, string | undefined]> = [
    ['url', config.url],
    ['command', config.command],
    ['cwd', config.cwd],
  ]

  for (const [path, value] of scalars) {
    if (value && hasSecretReference(value)) {
      errors.push({path, message: `Secrets cannot be used in ${path}. Use env or headers instead.`})
    }
  }

  for (const [index, arg] of (config.args ?? []).entries()) {
    if (hasSecretReference(arg)) {
      errors.push({path: `args.${index}`, message: 'Secrets cannot be used in arguments. Use env instead.'})
    }
  }

  return errors
}

function isValidRemoteUrl(value: string): boolean {
  try {
    // No base URL on purpose. A base would resolve a relative string into a
    // valid URL, which is exactly the input this check exists to reject.
    // eslint-disable-next-line no-restricted-syntax
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Drops the fields that do not belong to the chosen transport.
 *
 * Preserve opaque options while removing known fields for the other transport.
 */
export function pruneMcpServer(config: McpServerConfig): McpServerConfig {
  const next = {...config}
  const rejected = isRemoteTransport(config.type) ? FIELDS_REJECTED_BY_REMOTE : FIELDS_REJECTED_BY_PROCESS
  for (const field of rejected) delete next[field]
  for (const field of Object.keys(next)) {
    if (next[field] === undefined) delete next[field]
  }
  return next
}
